import { ref } from '@vue/composition-api'

import InvoicingService from '@services/InvoicingService'

// Module-level singleton — AppNavbar.vue, Invoicing.vue, and router.js all
// share this one reactive value instead of each fetching independently.
// Created lazily (on first useInvoiceAlerts() call) rather than at module
// scope: this file is reachable from router.js's top-level imports, which
// run before Vue.use(VueCompositionAPI) in main.js — calling ref() at
// import time would run ahead of the plugin being installed.
let alerts = null

// A dedicated, non-route signal for "the user asked to see the
// needs-attention/changed-after-approve set" — AppNavbar.vue's warning
// icon and Invoicing.vue's own banner set this (via requestFilter) and
// Invoicing.vue watches it to switch tab/filter. This exists because
// router-link navigation to a query that's already the current route is a
// silent no-op in Vue Router (no navigation, no route-query change) — so a
// second click on the icon while the URL already has
// ?filter=needs-attention would otherwise do nothing. A plain nonce-tagged
// ref update, by contrast, always fires its watcher.
let filterRequest = null
let filterRequestNonce = 0

let lastFetchedAt = 0
const MIN_REFRESH_INTERVAL_MS = 5000

// Days remaining until the end of the current month is queried against this
// threshold before we even hit the network — kept as an env var (rather than
// hardcoded 4) so it can be bumped up locally to test the alert without
// waiting for an actual month-end.
const ALERT_WINDOW_DAYS = Number(process.env.VUE_APP_INVOICE_ALERT_WINDOW_DAYS)

const daysUntilMonthEnd = () => {
  const now = new Date()
  const lastDayOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0)
  return lastDayOfMonth.getDate() - now.getDate()
}

async function refresh() {
  if (daysUntilMonthEnd() > ALERT_WINDOW_DAYS) return // outside the alert window — skip the call entirely

  const now = Date.now()
  if (now - lastFetchedAt < MIN_REFRESH_INTERVAL_MS) return // guard against duplicate calls on rapid-fire navigations
  lastFetchedAt = now
  try {
    const resp = await InvoicingService.getInvoiceAlerts()
    const inner = resp.data && resp.data.data
    alerts.value = inner && inner.data || alerts.value
  } catch (e) {
    // Fail soft — keep the last known value, never break navigation over this.
  }
}

function requestFilter(filter) {
  filterRequestNonce += 1
  filterRequest.value = { filter, nonce: filterRequestNonce }
}

export const useInvoiceAlerts = () => {
  if (!alerts) {
    alerts = ref({ needs_attention: false, changed_after_approve_count: 0 })
  }
  if (!filterRequest) {
    filterRequest = ref(null)
  }
  return {
    alerts,
    refresh,
    filterRequest,
    requestFilter,
  }
}
